import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../services/api.service';
import { Campaign } from '../models/types';
import { env } from '../config/env';

interface TestCall {
  id: string;
  campaign_name: string;
  direction: string;
  phone: string;
  status: string;
  detail: string;
  hangup: boolean;
  created_at: string;
  transcript: { speaker: string; text: string }[];
  metrics: { received_bytes?: number; played_bytes?: number };
}
interface TestState {
  calls: TestCall[];
  model: string;
  inbound_campaign_id: string | null;
  inbound_expires_at: string | null;
  worker: {
    online?: boolean;
    registered?: boolean;
    registration_code?: number;
    extension?: string;
    transport?: string;
    media_supported?: boolean;
    ai_configured?: boolean;
    model?: string;
  };
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="test-page">
      <header>
        <div>
          <p class="eyebrow">TEST STUDIO</p>
          <h1>Hear your agent in action</h1>
          <p>Choose a campaign, then call a phone or answer an incoming call.</p>
        </div>
        <button class="secondary" (click)="refresh()" [disabled]="refreshing">
          Refresh status
        </button>
      </header>
      <p class="error" role="alert" *ngIf="error()">{{ error() }}</p>
      <p class="notice" role="status" *ngIf="notice()">{{ notice() }}</p>
      <section class="health" *ngIf="state() as s">
        <span [class.good]="s.worker.online"
          >Worker {{ s.worker.online ? 'online' : 'offline' }}</span
        >
        <span [class.good]="s.worker.online && s.worker.registered"
          >SIP {{ s.worker.online && s.worker.registered ? 'registered' : 'not ready' }} ·
          {{ s.worker.registration_code || '—' }}</span
        >
        <span [class.good]="s.worker.online && s.worker.media_supported"
          >Audio
          {{ s.worker.online && s.worker.media_supported ? 'supported' : 'unavailable' }}</span
        >
        <span [class.good]="s.worker.online && s.worker.ai_configured"
          >AI {{ s.worker.online && s.worker.ai_configured ? 'configured' : 'not ready' }}</span
        >
      </section>
      <div class="columns">
        <section class="card controls">
          <h2>1. Choose your agent</h2>
          <label for="campaign">Campaign</label>
          <select id="campaign" [(ngModel)]="campaignId">
            <option value="">Select a campaign</option>
            <option *ngFor="let c of campaigns()" [value]="c.id">
              {{ c.name }} · {{ c.status }}
            </option>
          </select>
          <p *ngIf="!campaigns().length">
            No campaigns found. Create one with a script to define your AI agent.
          </p>
          <div class="agent" *ngIf="selected() as c">
            <strong>{{ c.gemini_voice }} · {{ c.language }}</strong>
            <details>
              <summary>Review agent instructions</summary>
              <p class="script" dir="auto">
                {{ c.script_text || 'This campaign needs a script before testing.' }}
              </p>
            </details>
            <small>Model: {{ state()?.model || 'Loading…' }} (shared application setting)</small>
          </div>
          <div class="ux-segments" aria-label="Test direction">
            <button (click)="mode = 'outbound'" [attr.aria-pressed]="mode === 'outbound'">
              Call a number</button
            ><button (click)="mode = 'inbound'" [attr.aria-pressed]="mode === 'inbound'">
              Receive a call
            </button>
          </div>
          <ng-container *ngIf="mode === 'outbound'"
            ><h2>2. Call a number</h2>
            <label for="phone">Phone number or extension</label>
            <input
              id="phone"
              type="tel"
              [(ngModel)]="phone"
              placeholder="e.g. 03001234567"
              autocomplete="tel"
            />
            <button
              (click)="dial()"
              [disabled]="busy() || !ready() || !validPhone() || !selected()?.script_text?.trim()"
            >
              {{ busy() ? 'Working…' : 'Call with AI' }}
            </button>
            <small
              >Calls the entered number immediately. Campaign leads and calling schedules are not
              used.</small
            >
            <small
              >Use the exact number format accepted by UCM/MicroSIP. Local 03… and international
              +92… may use different PBX routes.</small
            > </ng-container
          ><ng-container *ngIf="mode === 'inbound'"
            ><h2>2. Receive a test call</h2>
            <p>
              Route incoming calls on extension
              <strong>{{ state()?.worker?.extension || '—' }}</strong> to the selected agent for 30
              minutes.
            </p>
            <p *ngIf="state()?.inbound_campaign_id">
              <strong>Answering with {{ inboundName() }}</strong
              ><br />Until {{ state()?.inbound_expires_at | date: 'shortTime' }}
            </p>
            <button
              class="secondary"
              (click)="setInbound(campaignId)"
              [disabled]="busy() || !ready() || !selected()?.script_text?.trim()"
            >
              Use selected agent for incoming calls
            </button>
            <button
              class="secondary"
              *ngIf="state()?.inbound_campaign_id"
              (click)="setInbound(null)"
              [disabled]="busy()"
            >
              Stop receiving test calls
            </button>
            <small
              >This temporarily overrides the worker’s normal inbound campaign. Stopping restores
              normal routing.</small
            >
          </ng-container>
          <p class="ux-hint">
            Real phone call, simulated follow-up. Emails, support requests, consent changes and
            submissions are previews. Production campaigns continue independently.
          </p>
        </section>
        <section class="card conversation">
          <h2>Conversation & call history</h2>
          <div class="empty" *ngIf="!state()?.calls?.length">
            <h3>Your first test starts here</h3>
            <p>
              Live call state, audio counters, and the transcript appear here when you dial or
              receive a call.
            </p>
          </div>
          <article *ngFor="let call of state()?.calls" class="call">
            <div class="call-heading">
              <div>
                <strong>{{ call.phone }}</strong>
                <p>
                  {{ call.campaign_name }} · {{ call.direction }} ·
                  {{ call.created_at | date: 'shortTime' }}
                </p>
              </div>
              <span class="badge">{{ call.status.replace('_', ' ') }}</span>
            </div>
            <button
              class="danger"
              *ngIf="active(call)"
              (click)="hangup(call.id)"
              [disabled]="busy() || call.hangup"
            >
              {{ call.hangup ? 'Ending…' : 'Hang up' }}
            </button>
            <p class="detail">{{ call.detail }}</p>
            <details>
              <summary>Audio diagnostics</summary>
              <small
                >Phone → AI: {{ call.metrics.received_bytes || 0 }} bytes · AI → phone:
                {{ call.metrics.played_bytes || 0 }} bytes</small
              >
            </details>
            <details [open]="active(call)">
              <summary>Transcript ({{ call.transcript.length }})</summary>
              <div class="transcript">
                <p
                  *ngFor="let line of turns(call)"
                  [style.border-left]="
                    line.speaker === 'AI' ? '3px solid #146b5e' : '3px solid #718096'
                  "
                  style="padding:8px 12px"
                  dir="auto"
                >
                  <strong>{{
                    line.speaker === 'Caller' ? 'Caller / listener' : line.speaker
                  }}</strong
                  ><br />{{ line.text }}
                </p>
                <p *ngIf="!call.transcript.length">Waiting for speech…</p>
              </div>
            </details>
          </article>
        </section>
      </div>
    </div>
  `,
  styles: [
    `
      .test-page {
        max-width: 1250px;
        margin: auto;
        color: #153c38;
      }
      header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
      }
      h1 {
        font:
          2.2rem Georgia,
          serif;
        margin: 4px 0;
      }
      h2 {
        font-size: 1.1rem;
        margin: 8px 0;
      }
      p {
        line-height: 1.5;
        margin: 8px 0;
        color: #536a67;
      }
      .eyebrow {
        font-size: 11px;
        letter-spacing: 2px;
        font-weight: 700;
      }
      .health {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin: 22px 0;
      }
      .health span {
        padding: 8px 12px;
        border-radius: 24px;
        background: #fce9e5;
        color: #973e2c;
        font-size: 13px;
      }
      .health .good {
        background: #dff3e9;
        color: #176b49;
      }
      .columns {
        display: grid;
        grid-template-columns: minmax(280px, 390px) 1fr;
        gap: 22px;
      }
      .card {
        background: white;
        border: 1px solid #deebe6;
        border-radius: 18px;
        padding: 24px;
      }
      .controls {
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-self: start;
      }
      label {
        font-size: 13px;
        font-weight: 600;
      }
      input,
      select {
        padding: 12px;
        border: 1px solid #bccfc6;
        border-radius: 9px;
        width: 100%;
        box-sizing: border-box;
        font: inherit;
      }
      button {
        border: 0;
        border-radius: 9px;
        padding: 12px 16px;
        background: #146b5e;
        color: white;
        cursor: pointer;
        font-weight: 600;
      }
      button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .secondary {
        background: #e8f1ed;
        color: #24564a;
      }
      .danger {
        background: #fee6e0;
        color: #a43724;
      }
      .agent {
        padding: 14px;
        background: #f5f8f5;
        border-radius: 9px;
      }
      .script {
        max-height: 100px;
        overflow: auto;
        white-space: pre-wrap;
      }
      small {
        color: #647b75;
        line-height: 1.5;
      }
      .note {
        font-size: 12px;
      }
      .error {
        padding: 14px;
        background: #fff0ec;
        color: #9c3322;
        border-radius: 8px;
      }
      .notice {
        padding: 12px;
        background: #dff3e9;
        border-radius: 8px;
      }
      .call {
        border-top: 1px solid #e4ece8;
        padding: 20px 0;
      }
      .call-heading {
        display: flex;
        justify-content: space-between;
        gap: 12px;
      }
      .call-heading p {
        font-size: 12px;
      }
      .badge {
        background: #edf3f0;
        border-radius: 20px;
        padding: 6px 10px;
        align-self: start;
        font-size: 12px;
        white-space: nowrap;
      }
      .detail {
        font-size: 13px;
        overflow-wrap: anywhere;
      }
      .transcript {
        max-height: 320px;
        overflow: auto;
        padding: 8px 12px;
        background: #f8faf8;
        border-radius: 9px;
      }
      .transcript p {
        font-size: 14px;
        white-space: pre-wrap;
      }
      .transcript strong {
        font-size: 11px;
        color: #146b5e;
      }
      summary {
        cursor: pointer;
        margin: 14px 0;
        font-size: 13px;
      }
      .empty {
        padding: 60px 20px;
        text-align: center;
      }
      .empty h3 {
        font:
          1.5rem Georgia,
          serif;
      }
      hr {
        width: 100%;
        border: 0;
        border-top: 1px solid #e5ece7;
      }
      @media (max-width: 900px) {
        .columns {
          grid-template-columns: 1fr;
        }
        header {
          align-items: start;
          flex-direction: column;
        }
      }
    `,
  ],
})
export class TestCallPage implements OnInit, OnDestroy {
  turns(call: TestCall) {
    const result: { speaker: string; text: string }[] = [];
    for (const line of call.transcript) {
      const last = result[result.length - 1];
      if (last?.speaker === line.speaker) last.text += line.text;
      else result.push({ ...line });
    }
    return result;
  }
  campaigns = signal<Campaign[]>([]);
  state = signal<TestState | null>(null);
  error = signal('');
  notice = signal('');
  busy = signal(false);
  refreshing = false;
  mode: 'outbound' | 'inbound' = 'outbound';
  campaignId = '';
  phone = '';
  private timer?: ReturnType<typeof setInterval>;
  private base = `${env.apiUrl}/api/v1/test-calls`;
  constructor(
    private api: ApiService,
    private http: HttpClient,
    private route: ActivatedRoute,
  ) {}
  async ngOnInit() {
    try {
      this.campaigns.set(await this.api.listCampaigns());
    } catch {
      this.error.set('Could not load campaigns. Refresh the page to retry.');
    }
    const chosen = this.route.snapshot.queryParamMap.get('campaign');
    if (chosen && this.campaigns().some((c) => c.id === chosen)) {
      this.campaignId = chosen;
      this.mode = this.selected()?.campaign_type === 'inbound' ? 'inbound' : 'outbound';
    }
    await this.refresh();
    this.timer = setInterval(() => void this.refresh(), 2000);
  }
  ngOnDestroy() {
    if (this.timer) clearInterval(this.timer);
  }
  selected() {
    return this.campaigns().find((c) => c.id === this.campaignId);
  }
  inboundName() {
    return (
      this.campaigns().find((c) => c.id === this.state()?.inbound_campaign_id)?.name ||
      'selected campaign'
    );
  }
  ready() {
    const w = this.state()?.worker;
    return w?.online && w.registered && w.media_supported && w.ai_configured;
  }
  validPhone() {
    return /^\+?[0-9*#]{2,30}$/.test(this.phone.trim());
  }
  active(c: TestCall) {
    return !['completed', 'failed', 'cancelled', 'no_answer', 'busy'].includes(c.status);
  }
  async refresh() {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      this.state.set(await firstValueFrom(this.http.get<TestState>(this.base)));
    } catch {
      this.state.update((s) => (s ? { ...s, worker: { ...s.worker, online: false } } : s));
      this.error.set('Cannot refresh test-call status. Check the API connection.');
    } finally {
      this.refreshing = false;
    }
  }
  async action(request: () => Promise<unknown>, message: string) {
    if (this.busy()) return;
    this.busy.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      await request();
      this.notice.set(message);
      await this.refresh();
    } catch (e: any) {
      this.error.set(
        typeof e?.error?.detail === 'string'
          ? e.error.detail
          : 'Action failed. Check your connection and try again.',
      );
    } finally {
      this.busy.set(false);
    }
  }
  dial() {
    if (!this.validPhone() || !this.ready()) return;
    return this.action(
      () =>
        firstValueFrom(
          this.http.post(this.base, { campaign_id: this.campaignId, phone: this.phone.trim() }),
        ),
      'Test call queued. The worker will dial when a slot is available.',
    );
  }
  setInbound(id: string | null) {
    return this.action(
      () => firstValueFrom(this.http.put(`${this.base}/inbound`, { campaign_id: id })),
      id
        ? 'Incoming test route enabled for 30 minutes. Call the displayed SIP extension.'
        : 'Incoming test route disabled.',
    );
  }
  hangup(id: string) {
    return this.action(
      () => firstValueFrom(this.http.post(`${this.base}/${id}/hangup`, {})),
      'Hangup requested.',
    );
  }
}
